import { cn } from '@/shared/utilities';
import { Typography } from '@mui/material';
import dayjs, { type Dayjs } from 'dayjs';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface MessageHeaderProps {
  senderName: string;
  sentAt?: Date | string | number | Dayjs;
  isCompact?: boolean;
  isOwn?: boolean;
}

const MessageHeader = memo<MessageHeaderProps>(({ senderName, sentAt, isCompact, isOwn = false }) => {
  const { t } = useTranslation();
  const formatTime = useCallback((time: any) => dayjs(time).format('HH:mm'), []);

  // Memoize computed values
  const formattedTime = useMemo(() => (sentAt ? formatTime(sentAt) : ''), [sentAt, formatTime]);

  const displayName = useMemo(
    () => (isOwn ? t('messages.layout.you') : senderName || t('messages.layout.unknown')),
    [isOwn, senderName, t]
  );

  const ariaLabel = useMemo(() => {
    if (!sentAt) return '';
    return `Tin nhắn từ ${displayName} lúc ${formattedTime}`;
  }, [displayName, formattedTime]);

  if (!sentAt) return null;

  return (
    <Typography
      component='span'
      variant='inherit'
      className={cn(
        'mex:!font-medium mex:mb-1 mex:flex mex:items-end mex:gap-2 mex:text-text-primary',
        isOwn ? 'mex:flex-row-reverse' : 'mex:flex-row',
        isCompact ? 'mex:!text-[10px]' : 'mex:!text-[12px]'
      )}
      aria-label={ariaLabel}
    >
      <span>{displayName}</span>
      <span className='mex:!text-[10px] mex:text-text-secondary'>{formattedTime}</span>
    </Typography>
  );
});

MessageHeader.displayName = 'MessageHeader';

export { MessageHeader };
